import { MutationTree, ActionTree, ActionContext, GetterTree } from "vuex";
import { RootState } from "@/store";
import LicenseService from "@/services/licenseService";
import {
  License,
  LicenseDeserializer,
  LicenseData,
  LicenseDetailRoomDeserializer,
  LicenseDetailUserDeserializer,
} from "@/models/license";
import * as listable from "./listableModule";
import logger from "@/services/loggerService";
import clone from "lodash/clone";
import i18n from "@/i18n";
import router from "@/router";
import cloneDeep from "lodash/cloneDeep";

export interface LicenseState extends listable.ListableState {
  currentLicense: License | undefined;
  licenses: License[];
  includeTrials: boolean;
}

type LicenseContext = ActionContext<LicenseState, RootState>;

export interface AddLicensePayload {
  data: LicenseData;
  /** Reload the licenses table afterwards (the Licenses page). Default true; the wizard skips it. */
  refreshList?: boolean;
}

export const namespaced = true;

const initialState = {
  ...listable.state,
  ...{
    currentLicense: undefined,
    licenses: [],
    includeTrials: false,
  },
};

export const state = (): LicenseState => cloneDeep(initialState);

export const getters: GetterTree<LicenseState, RootState> = {
  ...listable.getters,
  ...{
    currentLicense: (state): License | undefined => {
      return state.currentLicense;
    },
    licenses: (state): License[] => {
      return state.licenses;
    },
    includeTrials: (state): boolean => {
      return state.includeTrials;
    },
  },
};

export const mutations: MutationTree<LicenseState> = {
  ...listable.mutations,
  ...{
    setCurrentLicense(state: LicenseState, license: License) {
      state.currentLicense = license;
    },
    resetCurrentLicense(state: LicenseState) {
      state.currentLicense = undefined;
    },
    setLicenses(state: LicenseState, licenses: License[]) {
      state.licenses = licenses;
    },
    addLicense(state: LicenseState, license: License) {
      state.licenses.push(license);
    },
    setIncludeTrials(state: LicenseState, includeTrials: boolean) {
      state.includeTrials = includeTrials;
    },
  },
};

export const actions: ActionTree<LicenseState, RootState> = {
  ...listable.actions,
  ...{
    async reset(context: LicenseContext): Promise<void> {
      const s = cloneDeep(initialState);
      context.state.currentLicense = s.currentLicense;
      context.state.licenses = s.licenses;
      context.state.includeTrials = s.includeTrials;
      context.dispatch("resetListable");
    },
    async fetchLicense(context: LicenseContext, id: string): Promise<void> {
      context.commit("setLoading", true);
      try {
        const res = await LicenseService.getLicense(id);
        context.commit("setCurrentLicense", res);
      } catch (e) {
        console.log(e);
      } finally {
        context.commit("setLoading", false);
      }
    },
    async fetchCurrentLicenseDetails(context: LicenseContext): Promise<void> {
      context.commit("setLoading", true);
      try {
        const license = clone(context.state.currentLicense);
        if (license && license.id) {
          if (license.isPod) {
            const details = await LicenseService.detailRoomSearch(
              license.id,
              context.state.queryOptions,
              context.state.searchString
            );
            context.commit("setTotalItems", details.count);
            license.detailRooms = details.items.map(
              LicenseDetailRoomDeserializer
            );
          } else {
            const details = await LicenseService.detailUserSearch(
              license.id,
              context.state.queryOptions,
              context.state.searchString
            );
            context.commit("setTotalItems", details.count);
            license.detailUsers = details.items.map(
              LicenseDetailUserDeserializer
            );
          }
          context.commit("setCurrentLicense", license);
        }
      } catch (e) {
        console.log(e);
      } finally {
        context.commit("setLoading", false);
      }
    },
    async fetchList(context: LicenseContext): Promise<void> {
      context.commit("setLoading", true);
      try {
        const res = await LicenseService.search(
          context.state.queryOptions,
          context.state.searchString,
          context.state.additionalFilters,
          context.state.includeTrials
        );
        context.commit("setLicenses", res.items.map(LicenseDeserializer));
        context.commit("setTotalItems", res.count);
      } catch (e) {
        logger.error(e);
      } finally {
        context.commit("setLoading", false);
      }
    },
    async addLicense(
      context: LicenseContext,
      payload: AddLicensePayload
    ): Promise<any> {
      const { data, refreshList = true } = payload;
      context.commit("setLoading", true);
      try {
        const res = await LicenseService.post(data);
        const lic = LicenseDeserializer(res.data);
        context.commit("addLicense", lic);
        context.commit(
          "notifications/displayNotification",
          {
            message: i18n.t("licensesList.created"),
            type: "success",
          },
          { root: true }
        );
        if (refreshList) context.dispatch("fetchList");
        return lic;
      } catch (e) {
        logger.error(e);
        let message = e.response.data.message ? e.response.data.message : e;
        if (e.response.data.errorCode == "L422") {
          message = i18n.t("licensesList.alreadyAssociated");
        }
        context.commit(
          "notifications/displayNotification",
          {
            message: message,
            type: "error",
          },
          { root: true }
        );
      } finally {
        context.commit("setLoading", false);
      }
    },
    async clearSeats(context: LicenseContext, id: string): Promise<void> {
      await LicenseService.clearSeats(id);
      return context.dispatch("fetchLicense", id);
    },
    async updateIncludeTrials(
      context: LicenseContext,
      includeTrials: boolean
    ): Promise<void> {
      context.commit("setIncludeTrials", includeTrials);
      context.state.queryOptions.page = 1;
      return context.dispatch("fetchList");
    },
    async clearExtensions(context: LicenseContext, id: string): Promise<void> {
      await LicenseService.clearExtensions(id);
      return context.dispatch("fetchLicense", id);
    },
    async deleteLicense(context: LicenseContext, id: string): Promise<void> {
      await LicenseService.deleteLicense(id);
      router.push(`/licenses`);
    },
    // //OVERRIDE listable action
    // async updateSearchString(
    //   context: LicenseContext,
    //   search: string
    // ): Promise<void> {
    //   context.commit("setSearchString", search);
    //   if (context.state.currentLicense) {
    //     return context.dispatch("fetchCurrentLicenseDetails");
    //   }
    //   return context.dispatch("fetchList");
    // },
    // //OVERRIDE listable action
    // async updateQueryOptions(
    //   context: LicenseContext,
    //   options: OptionsTable
    // ): Promise<void> {
    //   context.commit("setQueryOptions", options);
    //   if (context.state.currentLicense) {
    //     return context.dispatch("fetchCurrentLicenseDetails");
    //   }
    //   return context.dispatch("fetchList");
    // },
  },
};
